import React, { useState } from 'react'
import { Paper, TextField, Button, Typography, Box, Alert, CircularProgress } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { MfaVerificationDialog } from './MfaVerificationDialog'
import { MfaVerifyResponse } from '../../services/authService'

interface LoginFormProps {
  onSuccess?: () => void
  onSwitchToRegister?: () => void
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false)
  const [mfaSessionToken, setMfaSessionToken] = useState('')
  const { login, isLoading, setUser, setToken, refreshAccessibleFeatures } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !password) {
      setError('メールアドレスとパスワードを入力してください')
      return
    }

    try {
      const result = await login(email, password)

      // MFAが必要な場合
      if ('requiresMfa' in result && result.requiresMfa) {
        setMfaSessionToken(result.mfaSessionToken)
        setMfaDialogOpen(true)
        return
      }

      console.log('Login successful:', result)

      // 初回ログインでもダッシュボードにリダイレクト（受講機能利用可能）
      console.log('Redirecting to dashboard...')
      if ('firstLogin' in result && result.firstLogin) {
        console.log('First login detected - password change recommended but not required')
      }
      // 全ユーザーをダッシュボードにリダイレクト
      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/dashboard')
      }
    } catch (error: any) {
      console.error('Login error:', error)
      const errorMessage = error.response?.data?.error || 'ログインに失敗しました'
      setError(errorMessage)
    }
  }

  const handleMfaSuccess = async (response: MfaVerifyResponse) => {
    setMfaDialogOpen(false)
    setMfaSessionToken('')

    // トークンとユーザー情報を更新
    if (response.token && response.user) {
      setToken(response.token)
      setUser(response.user)
      // アクセス可能な機能を取得
      await refreshAccessibleFeatures(response.user.id)
    }

    // バックアップコード残数の警告表示
    if (response.warning) {
      console.warn('Backup codes warning:', response.warning)
    }

    console.log('MFA verification successful')
    if (onSuccess) {
      onSuccess()
    } else {
      navigate('/dashboard')
    }
  }

  const handleMfaClose = () => {
    setMfaDialogOpen(false)
    setMfaSessionToken('')
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: { xs: 2.5, sm: 4 },
        maxWidth: 400,
        width: '100%',
        margin: 'auto',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}
      >
        <Typography variant="h5" component="h1" textAlign="center" sx={{ mb: { xs: 0, sm: 1 } }}>
          ログイン
        </Typography>

        <Typography
          variant="body2"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: { xs: 0.5, sm: 1 } }}
        >
          ComplianceAuto
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: { xs: 1, sm: 2 } }}>
            {error}
          </Alert>
        )}

        <TextField
          label="メールアドレス"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          autoComplete="email"
          autoFocus
          disabled={isLoading}
        />

        <TextField
          label="パスワード"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
          autoComplete="current-password"
          disabled={isLoading}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isLoading}
          sx={{ mt: { xs: 1, sm: 2 } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'ログイン'}
        </Button>

        {/* パスワードリセットリンク */}
        <Box sx={{ textAlign: 'center', pt: { xs: 0.5, sm: 1 } }}>
          <Button
            variant="text"
            onClick={() => navigate('/reset-password')}
            disabled={isLoading}
            sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            パスワードを忘れた方はこちら
          </Button>
        </Box>

        {/* 新規登録への切り替え */}
        {onSwitchToRegister && (
          <Box sx={{ textAlign: 'center', pt: { xs: 0, sm: 1 } }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              企業の新規登録はこちら
              <Button
                variant="text"
                onClick={onSwitchToRegister}
                disabled={isLoading}
                sx={{ ml: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                新規企業登録
              </Button>
            </Typography>
          </Box>
        )}
      </Box>

      {/* MFA検証ダイアログ */}
      <MfaVerificationDialog
        open={mfaDialogOpen}
        mfaSessionToken={mfaSessionToken}
        onClose={handleMfaClose}
        onSuccess={handleMfaSuccess}
      />
    </Paper>
  )
}
